import { toast } from "sonner";
import { getTranslation } from "../i18n/i18n";
import { getCurrentLanguage, setCurrentLanguage } from "@/services/languageService";

// 错误消息常量
export const ERROR_MESSAGES = {
  noMatchingPokemon: "noMatchingPokemon",
  partialMatchingPokemon: "partialMatchingPokemon",
  generatePokemonError: "generatePokemonError",
  fetchTypesError: "fetchTypesError"
} as const;

interface NamedApiResource {
  name: string;
  url: string;
}

export interface PokemonMoveVersionGroupDetail {
  level_learned_at: number;
  move_learn_method: NamedApiResource;
  order: number | null;
  version_group: NamedApiResource;
}

export interface PokemonMove {
  move: NamedApiResource;
  version_group_details: PokemonMoveVersionGroupDetail[];
}

export interface MoveDetail {
  id: number;
  name: string;
  accuracy: number | null;
  power: number | null;
  pp: number | null;
  priority: number;
  type: NamedApiResource;
  damage_class: NamedApiResource;
  effect_chance?: number | null;
  effect_entries?: Array<{
    effect: string;
    short_effect: string;
    language: NamedApiResource;
  }>;
  names?: Array<{
    name: string;
    language: NamedApiResource;
  }>;
}

// Types for Pokemon data
export interface Pokemon {
  id: number;
  name: string;
  types: {
    type: {
      name: string;
    };
  }[];
  sprites: {
    front_default?: string | null;
    other: {
      'official-artwork'?: {
        front_default?: string | null;
      };
      home?: {
        front_default?: string | null;
      };
    };
  };
  weight: number;
  height: number;
  stats: {
    base_stat: number;
    stat: {
      name: string;
    };
  }[];
  abilities: {
    ability: {
      name: string;
      localizedNames?: Record<string, string>;
    };
    is_hidden: boolean;
  }[];
  moves: PokemonMove[];
  base_experience: number;
  rarity?: number;
  localizedNames?: Record<string, string>;
  isLoadingNames?: boolean;
  species?: {
    url: string;
  };
  generation?: string;
}

export interface PokemonSpecies {
  generation: {
    name: string;
    url: string;
  };
  gender_rate: number;
  is_legendary: boolean;
  is_mythical: boolean;
  names?: Array<{
    name: string;
    language: {
      name: string;
      url: string;
    };
  }>;
  evolution_chain: {
    url: string;
  };
}

export interface PokemonType {
  name: string;
  url: string;
}

// Filter parameters
export interface PokemonFilters {
  type?: string;
  generation?: string;
  evolutionStage?: 'all' | 'fully-evolved' | 'not-fully-evolved';
  gender?: string;
  ability?: string;
  rarity?: string;
}

interface FetchRandomPokemonOptions {
  excludeIds?: number[];
}

// --- 缓存定义 ---
const pokemonCache: Map<number, {data: Pokemon, timestamp: number}> = new Map();
const speciesCache: Map<number | string, {data: any, timestamp: number}> = new Map();
const evolutionChainCache: Map<string, {data: any, timestamp: number}> = new Map();
const moveCache: Map<string, {data: MoveDetail, timestamp: number}> = new Map();
const pokemonBasicInfoCache: { data: Map<number, PokemonBasicInfo> | null, timestamp: number } = { data: null, timestamp: 0 };
const CACHE_DURATION = 60 * 60 * 1000;
const BASE_INFO_CACHE_DURATION = 24 * 60 * 60 * 1000;

// 新增：原始宝可梦列表缓存
const rawPokemonListCache: { data: Array<{ id: number; name: string; pokemonUrl: string; speciesUrl: string; }>, timestamp: number } = { data: [], timestamp: 0 };
const RAW_LIST_CACHE_DURATION = 24 * 60 * 60 * 1000; // 缓存24小时

// 新增：基础宝可梦信息接口
export interface PokemonBasicInfo {
  id: number;
  name: string;
  pokemonUrl: string;
  speciesUrl: string;
  types: string[];
  generation: string;
  localizedNames?: Record<string, string>;
}

let lastCacheCleanupTime = 0;
const CACHE_CLEANUP_INTERVAL = 24 * 60 * 60 * 1000;
let activeLanguage: string | null = null;

/**
 * 初始化语言监听器
 */
export const initializeLanguageListener = (): void => {
  try {
    const currentLanguage = getCurrentLanguage();
    if (activeLanguage !== currentLanguage) {
      console.debug(`🌐 Language changed: ${activeLanguage || '(initial)'} -> ${currentLanguage}`);
      activeLanguage = currentLanguage;
      cleanSpeciesCache();
    }
  } catch (error) {
    console.error('❌ Failed to initialize language listener:', error);
  }
};

/**
 * 检测语言变更并根据需要更新缓存
 */
export const checkLanguageChange = (): boolean => {
  try {
    const currentLanguage = getCurrentLanguage();
    if (activeLanguage !== null && activeLanguage !== currentLanguage) {
      console.debug(`🌐 Language change detected: ${activeLanguage} -> ${currentLanguage}`);
      activeLanguage = currentLanguage;
      cleanSpeciesCache();
      return true;
    }
    if (activeLanguage === null) {
      activeLanguage = currentLanguage;
    }
    return false;
  } catch (error) {
    console.error('❌ Failed to check language change:', error);
    return false;
  }
};

/**
 * 清理特定于语言的缓存（Species数据）
 */
export const cleanSpeciesCache = (): void => {
  try {
    console.debug('🧹 Clearing Species cache due to language change...');
    const speciesCacheSize = speciesCache.size;
    speciesCache.forEach((_, key) => {
      if (typeof key === 'number' || (typeof key === 'string' && key.includes('species'))) {
        speciesCache.delete(key);
      }
    });
    console.debug(`✅ Cleared Species cache in memory: ${speciesCacheSize} items originally`);

    try {
      let localStorageCleanedItems = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('species_')) {
          localStorage.removeItem(key);
          localStorageCleanedItems++;
        }
      }
      console.debug(`✅ Cleared localStorage species cache: ${localStorageCleanedItems} items`);
    } catch (storageError) {
      console.error('❌ Failed to clear localStorage species cache:', storageError);
    }
  } catch (error) {
    console.error('❌ Failed to clean species cache:', error);
  }
};

/**
 * 检查并清理过期的缓存项
 */
export const cleanupExpiredCache = (force: boolean = false): number => {
  try {
    const now = Date.now();
    let cleanedItems = 0;
    const shouldDoFullCleanup = force || (now - lastCacheCleanupTime > CACHE_CLEANUP_INTERVAL);

    if (shouldDoFullCleanup) {
      console.debug('🧹 Starting periodic cache cleanup...');
      lastCacheCleanupTime = now;
    } else {
      console.debug('🔍 Checking for expired cache items...');
    }

    for (const [id, cacheItem] of pokemonCache.entries()) {
      if (force || now - cacheItem.timestamp > CACHE_DURATION) {
        pokemonCache.delete(id);
        cleanedItems++;
      }
    }
    for (const [id, cacheItem] of speciesCache.entries()) {
      if (force || now - cacheItem.timestamp > CACHE_DURATION) {
        speciesCache.delete(id);
        cleanedItems++;
      }
    }
    for (const [url, cacheItem] of evolutionChainCache.entries()) {
      if (force || now - cacheItem.timestamp > CACHE_DURATION) {
        evolutionChainCache.delete(url);
        cleanedItems++;
      }
    }
    if (pokemonBasicInfoCache.data && (force || now - pokemonBasicInfoCache.timestamp > BASE_INFO_CACHE_DURATION)) {
      console.debug('🧹 Clearing basic Pokémon info cache...');
      pokemonBasicInfoCache.data = null;
      pokemonBasicInfoCache.timestamp = 0;
      cleanedItems++;
    }

    if (shouldDoFullCleanup) {
      try {
        let localStorageCleanedItems = 0;
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('pokemon_') || key.startsWith('species_') || key.startsWith('evolutionChain_') || key === 'pokemonBasicInfo_')) {
            try {
              const itemStr = localStorage.getItem(key);
              if (!itemStr) continue;
              const item = JSON.parse(itemStr);
              const duration = key === 'pokemonBasicInfo_' ? BASE_INFO_CACHE_DURATION : CACHE_DURATION;
              if (force || !item.timestamp || now - item.timestamp > duration) {
                localStorage.removeItem(key);
                localStorageCleanedItems++;
              }
            } catch (e) {
              localStorage.removeItem(key);
              localStorageCleanedItems++; // Count as cleaned if parsing failed and removed
            }
          }
        }
        cleanedItems += localStorageCleanedItems;
        if (localStorageCleanedItems > 0) {
          console.debug(`✅ Cleared localStorage expired cache: ${localStorageCleanedItems} items`);
        }
      } catch (storageError) {
        console.error('❌ Failed to clear localStorage cache:', storageError);
      }
      if(cleanedItems > 0){
        console.debug(`🎉 Cache cleanup complete, cleared ${cleanedItems} items (memory + LS)`);
      }
    }

    return cleanedItems;
  } catch (error) {
    console.error('❌ Failed to cleanup cache:', error);
    return 0;
  }
};

/**
 * 清除所有宝可梦相关缓存
 */
export const clearPokemonCaches = (): void => {
  try {
    console.debug('🧹 Starting to clear all Pokémon related caches...');

    const pokemonCacheSize = pokemonCache.size;
    const speciesCacheSize = speciesCache.size;
    const evolutionChainCacheSize = evolutionChainCache.size;
    const moveCacheSize = moveCache.size;
    const basicInfoExists = !!pokemonBasicInfoCache.data;

    pokemonCache.clear();
    speciesCache.clear();
    evolutionChainCache.clear();
    moveCache.clear();
    pokemonBasicInfoCache.data = null;
    pokemonBasicInfoCache.timestamp = 0;

    console.debug(`✅ Cleared memory caches: ${pokemonCacheSize} Pokemon, ${speciesCacheSize} Species, ${evolutionChainCacheSize} Chains, ${moveCacheSize} Moves, BasicInfo: ${basicInfoExists}`);

    try {
      let localStorageCleanedItems = 0;
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('pokemon_') || key.startsWith('species_') || key.startsWith('evolutionChain_') || key === 'pokemonBasicInfo_')) {
          localStorage.removeItem(key);
          localStorageCleanedItems++;
        }
      }
      console.debug(`✅ Cleared localStorage cache: ${localStorageCleanedItems} items`);
    } catch (storageError) {
      console.error('❌ Failed to clear localStorage cache:', storageError);
    }

    lastCacheCleanupTime = Date.now(); // Reset cleanup timer as we just did a full clean
    console.debug('🎉 All Pokémon related caches have been cleared');
  } catch (error) {
    console.error('❌ Failed to clear Pokémon caches:', error);
  }
};

/**
 * 语言代码映射表
 */
export const LANGUAGE_CODE_MAPPING: Record<string, string[]> = {
  'en': ['en'],
  'ja': ['ja', 'ja-hrkt', 'ja-roma'],
  'ko': ['ko'],
  'zh-CN': ['zh-hans', 'zh'],
  'zh-TW': ['zh-hant'],
  'fr': ['fr'],
  'de': ['de'],
  'es': ['es', 'es-ES', 'es-MX'],
  'it': ['it'],
  // PokeAPI uses pt-BR for Portuguese
  'pt': ['pt-br', 'pt'],
  'ru': ['ru'],
  'ar': ['ar'],
  'hi': ['hi'],
  'id': ['id'],
  'tr': ['tr'],
  'vi': ['vi']
};

/**
 * 获取并缓存基础宝可梦信息
 */
export const getPokemonBasicInfoMap = async (): Promise<Map<number, PokemonBasicInfo>> => {
  return getPokemonBasicInfoMapWithOptions({ buildIfMissing: true });
};

export const getPokemonBasicInfoMapWithOptions = async (options?: {
  /**
   * When false, only return cached data (memory/localStorage). Never build via API.
   * This avoids heavy work that can stall page load.
   */
  buildIfMissing?: boolean;
}): Promise<Map<number, PokemonBasicInfo>> => {
  const now = Date.now();
  if (pokemonBasicInfoCache.data && now - pokemonBasicInfoCache.timestamp < BASE_INFO_CACHE_DURATION) {
    console.debug("🚀 Using basic Pokémon info from memory cache");
    return pokemonBasicInfoCache.data;
  }
  try {
    const cachedStr = localStorage.getItem('pokemonBasicInfo_');
    if (cachedStr) {
      const cached = JSON.parse(cachedStr);
      if (now - cached.timestamp < BASE_INFO_CACHE_DURATION) {
        const hasAnyLocalizedNames = (data: any): boolean => {
          if (!data || typeof data !== "object") return false;
          for (const value of Object.values(data)) {
            const localizedNames = (value as any)?.localizedNames;
            if (localizedNames && typeof localizedNames === "object" && Object.keys(localizedNames).length > 0) {
              return true;
            }
          }
          return false;
        };

        // If cache was built by an older version (no localizedNames), invalidate to rebuild.
        if (!hasAnyLocalizedNames(cached.data)) {
          console.debug("🧹 Basic info LocalStorage cache missing localized names; rebuilding.");
          localStorage.removeItem('pokemonBasicInfo_');
        } else {
        console.debug("💾 Using basic Pokémon info from LocalStorage cache");
        const mapData = new Map<number, PokemonBasicInfo>();
        Object.entries(cached.data).forEach(([key, value]) => {
          mapData.set(parseInt(key), value as PokemonBasicInfo);
        });
        pokemonBasicInfoCache.data = mapData;
        pokemonBasicInfoCache.timestamp = cached.timestamp;
        return mapData;
        }
      } else {
        localStorage.removeItem('pokemonBasicInfo_');
        console.debug("Basic info LocalStorage cache expired");
      }
    }
  } catch (e) {
    console.error("Failed to read basic info from LocalStorage cache:", e);
    localStorage.removeItem('pokemonBasicInfo_'); // Attempt to remove corrupted item
  }

  if (options?.buildIfMissing === false) {
    console.debug("⏭️ Basic info cache miss; buildIfMissing=false, returning empty map.");
    return new Map();
  }

  console.log("🛠️ Building basic Pokémon info... This may take some time.");
  toast.info("Preparing base data, please wait..."); // User-facing, potentially i18n

  let isDataBuilt = false;

  try {
    const response = await fetch(`https://pokeapi.co/api/v2/pokemon?limit=1500`);
    if (!response.ok) throw new Error(`Failed to fetch base pokemon list: ${response.status}`);
    const listData = await response.json();
    const results: { name: string, url: string }[] = listData.results;

    const basicInfoMap = new Map<number, PokemonBasicInfo>();
    const promises: Promise<void>[] = [];
    let fetchErrors = 0;

    console.debug(`Fetching details for ${results.length} base records...`);

    const buildLocalizedNamesFromSpecies = (speciesData: any): Record<string, string> | undefined => {
      const namesArray = speciesData?.names;
      if (!Array.isArray(namesArray) || namesArray.length === 0) {
        return undefined;
      }

      const localizedNames: Record<string, string> = {};
      namesArray.forEach((nameObj: any) => {
        if (nameObj?.language?.name && nameObj.name) {
          localizedNames[nameObj.language.name] = nameObj.name;
        }
      });

        // Map app language codes to best matching API codes (e.g. zh-CN -> zh-hans/zh)
      Object.entries(LANGUAGE_CODE_MAPPING).forEach(([appLangCode, apiLangCodes]) => {
        if (!localizedNames[appLangCode]) {
          for (const apiLangCode of apiLangCodes) {
              const normalizedApiLangCode = apiLangCode.toLowerCase();
              if (localizedNames[normalizedApiLangCode]) {
                localizedNames[appLangCode] = localizedNames[normalizedApiLangCode];
              break;
            }
          }
        }
      });

      return Object.keys(localizedNames).length > 0 ? localizedNames : undefined;
    };

    for (const basic of results) {
      const idMatch = basic.url.match(/\/(\d+)\/?$/);
      if (!idMatch) continue;
      const id = parseInt(idMatch[1]);
      if(id <= 0 || id > 1025) { 
        continue;
      }
      const pokemonUrl = basic.url;
      const speciesUrl = `https://pokeapi.co/api/v2/pokemon-species/${id}/`;

      promises.push(
        Promise.allSettled([
          fetch(pokemonUrl).then(res => res.ok ? res.json() : Promise.reject(`Pokemon ${id} status ${res.status}`)),
          fetch(speciesUrl).then(res => res.ok ? res.json() : Promise.reject(`Species ${id} status ${res.status}`))
        ]).then(([pokemonResult, speciesResult]) => {
          if (pokemonResult.status === 'fulfilled' && speciesResult.status === 'fulfilled') {
            const pokemonData = pokemonResult.value;
            const speciesData = speciesResult.value;
            if(pokemonData.types && speciesData.generation?.name){
              const types = pokemonData.types.map((t: any) => t.type.name);
              const generation = speciesData.generation.name;
              const localizedNames = buildLocalizedNamesFromSpecies(speciesData);
              basicInfoMap.set(id, {
                id,
                name: basic.name,
                pokemonUrl,
                speciesUrl,
                types,
                generation,
                localizedNames
              });
              isDataBuilt = true;
            } else {
              fetchErrors++;
            }
          } else {
            fetchErrors++;
          }
        })
      );
    }

    const concurrencyLimit = 5;
    let completed = 0;
    console.debug(`Starting batched fetches with concurrency ${concurrencyLimit}...`);
    for (let i = 0; i < promises.length; i += concurrencyLimit) {
      const batch = promises.slice(i, i + concurrencyLimit);
      await Promise.all(batch);
      completed += batch.length;
      if (completed % (concurrencyLimit * 20) === 0 || completed === promises.length) { 
        console.debug(`Base info build progress: ${completed}/${promises.length}`);
      }
      await new Promise(resolve => setTimeout(resolve, 100)); // Brief pause
    }
    console.debug(`All batches processed. Total errors: ${fetchErrors}`);

    if (!isDataBuilt || basicInfoMap.size === 0) {
      throw new Error(`Failed to build any basic Pokémon info (Errors: ${fetchErrors})`);
    }

    console.log(`✅ Successfully built ${basicInfoMap.size} basic Pokémon info entries.`);
    toast.success(`Base data prepared!`); // User-facing, potentially i18n

    pokemonBasicInfoCache.data = basicInfoMap;
    pokemonBasicInfoCache.timestamp = Date.now();

    try {
      const dataToStore = Object.fromEntries(basicInfoMap.entries());
      localStorage.setItem('pokemonBasicInfo_', JSON.stringify({
        data: dataToStore,
        timestamp: pokemonBasicInfoCache.timestamp
      }));
      console.debug("Basic info saved to LocalStorage");
    } catch (e) {
      console.error("Failed to write basic info to LocalStorage (possibly exceeded size limit):", e);
    }

    return basicInfoMap;
  } catch (error) {
    console.error("❌ Failed to build basic Pokémon info:", error);
    toast.error("Error preparing base data, some features might be limited."); // User-facing, potentially i18n
    return new Map();
  }
};

/**
 * 获取宝可梦物种数据的最小集
 */
export const fetchPokemonSpeciesMinimal = async (id: number): Promise<any | null> => {
  const cacheKey = `species_${id}_minimal`;
  const now = Date.now();

  if (speciesCache.has(cacheKey) && now - speciesCache.get(cacheKey)!.timestamp < CACHE_DURATION) {
    return speciesCache.get(cacheKey)!.data;
  }
  if (speciesCache.has(id) && now - speciesCache.get(id)!.timestamp < CACHE_DURATION) {
    const fullSpeciesData = speciesCache.get(id)!.data;
    if (fullSpeciesData.is_legendary !== undefined && fullSpeciesData.is_mythical !== undefined && fullSpeciesData.evolution_chain?.url && fullSpeciesData.generation?.name) {
      const minimalData = {
        is_legendary: fullSpeciesData.is_legendary,
        is_mythical: fullSpeciesData.is_mythical,
        evolution_chain: { url: fullSpeciesData.evolution_chain.url },
        generation: { name: fullSpeciesData.generation.name },
      };
      speciesCache.set(cacheKey, { data: minimalData, timestamp: now });
      return minimalData;
    }
  }

  try {
    const speciesUrl = `https://pokeapi.co/api/v2/pokemon-species/${id}/`;
    const response = await fetch(speciesUrl);
    if (!response.ok) throw new Error(`Status ${response.status}`);
    const fullSpeciesData = await response.json();

    if (fullSpeciesData.is_legendary === undefined || fullSpeciesData.is_mythical === undefined || !fullSpeciesData.evolution_chain?.url || !fullSpeciesData.generation?.name) {
      throw new Error("Species data structure mismatch");
    }

    const minimalData = {
      is_legendary: fullSpeciesData.is_legendary,
      is_mythical: fullSpeciesData.is_mythical,
      evolution_chain: { url: fullSpeciesData.evolution_chain.url },
      generation: { name: fullSpeciesData.generation.name },
    };

    speciesCache.set(cacheKey, { data: minimalData, timestamp: now });
    speciesCache.set(id, { data: fullSpeciesData, timestamp: now });

    return minimalData;
  } catch (error) {
    return null;
  }
};

/**
 * 获取宝可梦详情数据的最小集
 */
export const fetchPokemonDetailsMinimal = async (id: number): Promise<any | null> => {
  if (pokemonCache.has(id) && Date.now() - pokemonCache.get(id)!.timestamp < CACHE_DURATION) {
    const fullData = pokemonCache.get(id)!.data;
    if (fullData.base_experience !== undefined) {
      return { base_experience: fullData.base_experience };
    }
  }

  try {
    const pokemonUrl = `https://pokeapi.co/api/v2/pokemon/${id}/`;
    const response = await fetch(pokemonUrl);
    if (!response.ok) throw new Error(`Status ${response.status}`);
    const fullData = await response.json();

    if (fullData.base_experience === undefined) {
      throw new Error("Pokemon data structure mismatch - missing base_experience");
    }

    const minimalData = {
      base_experience: fullData.base_experience,
    };

    pokemonCache.set(id, { data: fullData as Pokemon, timestamp: Date.now() });

    return minimalData;
  } catch (error) {
    return null;
  }
};

/**
 * 获取进化链数据
 */
export const fetchEvolutionChain = async (chainUrl: string): Promise<any | null> => {
  if (!chainUrl) return null;
  const now = Date.now();
  if (evolutionChainCache.has(chainUrl) && now - evolutionChainCache.get(chainUrl)!.timestamp < CACHE_DURATION) {
    return evolutionChainCache.get(chainUrl)!.data;
  }

  try {
    const response = await fetch(chainUrl);
    if (!response.ok) throw new Error(`Status ${response.status}`);
    const chainData = await response.json();

    if (!chainData.chain) {
      throw new Error("Evolution chain data structure mismatch");
    }

    evolutionChainCache.set(chainUrl, { data: chainData, timestamp: now });

    return chainData;
  } catch (error) {
    return null;
  }
};

/**
 * 根据规则计算稀有度
 */
export const calculateRarity = (pokemonMinimal: any, speciesMinimal: any): number => {
  if (!pokemonMinimal || !speciesMinimal) return 1;

  let rarityScore = 1;
  if (speciesMinimal.is_legendary === true) rarityScore += 4;
  if (speciesMinimal.is_mythical === true) rarityScore += 5;

  const baseExp = typeof pokemonMinimal.base_experience === 'number' ? pokemonMinimal.base_experience : 0;
  const expContribution = Math.min(Math.floor(baseExp / 35), 3);
  rarityScore += expContribution;

  return Math.max(1, Math.min(10, rarityScore));
};

/**
 * 检查宝可梦在进化链中的阶段
 */
export const checkEvolutionStage = (speciesName: string, evolutionChainData: any): 'fully-evolved' | 'not-fully-evolved' | null => {
  if (!evolutionChainData || !evolutionChainData.chain || !speciesName) return null;

  let isFullyEvolved: boolean | null = null;

  const findInChain = (node: any): boolean => {
    if (!node || !node.species) return false;

    if (node.species.name === speciesName) {
      isFullyEvolved = !node.evolves_to || node.evolves_to.length === 0;
      return true;
    }

    if (node.evolves_to && node.evolves_to.length > 0) {
      for (const nextNode of node.evolves_to) {
        if (findInChain(nextNode)) {
          return true;
        }
      }
    }
    return false;
  };

  const found = findInChain(evolutionChainData.chain);

  if (!found || isFullyEvolved === null) {
    return null;
  }

  return isFullyEvolved ? 'fully-evolved' : 'not-fully-evolved';
};

/**
 * 简化版 fetchPokemon
 */
export const fetchPokemon = async (idOrName: number | string): Promise<Pokemon> => {
  const id = typeof idOrName === 'string' ? idOrName.toLowerCase() : idOrName;
  const now = Date.now();

  if (typeof id === 'number' && pokemonCache.has(id)) {
    const cached = pokemonCache.get(id)!;
    if (now - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
  }

  try {
    const response = await fetch(`https://pokeapi.co/api/v2/pokemon/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch Pokémon ${id}: ${response.status}`);
    }
    const data = await response.json();

    if(!data.id || !data.name || !data.types || !data.sprites){
      throw new Error(`Incomplete Pokemon data received for ${id}`);
    }

    const pokemonData = data as Pokemon;

    pokemonData.localizedNames = undefined;
    pokemonData.isLoadingNames = false;

    pokemonCache.set(pokemonData.id, { data: pokemonData, timestamp: now });

    return pokemonData;
  } catch (error) {
    console.error(`Error in fetchPokemon for ${id}:`, error);
    throw error;
  }
};

export const fetchMoveDetails = async (idOrName: number | string): Promise<MoveDetail> => {
  const cacheKey = typeof idOrName === "string" ? idOrName.toLowerCase() : String(idOrName);
  const now = Date.now();

  if (moveCache.has(cacheKey)) {
    const cached = moveCache.get(cacheKey)!;
    if (now - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
  }

  try {
    const response = await fetch(`https://pokeapi.co/api/v2/move/${cacheKey}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch move ${cacheKey}: ${response.status}`);
    }

    const data = await response.json();

    if (!data.id || !data.name || !data.type || !data.damage_class) {
      throw new Error(`Incomplete move data received for ${cacheKey}`);
    }

    const moveData = data as MoveDetail;
    moveCache.set(cacheKey, { data: moveData, timestamp: now });
    moveCache.set(String(moveData.id), { data: moveData, timestamp: now });

    return moveData;
  } catch (error) {
    console.error(`Error in fetchMoveDetails for ${cacheKey}:`, error);
    throw error;
  }
};

export const fetchBatchMoveDetails = async (
  moveNamesOrIds: Array<number | string>
): Promise<MoveDetail[]> => {
  const uniqueMoveKeys = Array.from(
    new Set(
      moveNamesOrIds
        .map((value) => (typeof value === "string" ? value.toLowerCase() : value))
        .filter((value): value is number | string => value !== "")
    )
  );

  const results = await Promise.allSettled(uniqueMoveKeys.map((moveKey) => fetchMoveDetails(moveKey)));

  return results
    .filter((result): result is PromiseFulfilledResult<MoveDetail> => result.status === "fulfilled")
    .map((result) => result.value);
};

/**
 * 获取技能详情的本地化名称
 */
export const fetchAbilityLocalization = async (abilityName: string, currentLanguage: string): Promise<string> => {
  const cacheKey = `ability_${abilityName}`;
  const now = Date.now();
  
  // 检查缓存
  if (moveCache.has(cacheKey)) {
    const cached = moveCache.get(cacheKey)!;
    if (now - cached.timestamp < CACHE_DURATION) {
      const abilityData = cached.data as any;
      if (abilityData?.names) {
        const localizedNames: Record<string, string> = {};
        abilityData.names.forEach((nameObj: any) => {
          if (nameObj?.language?.name && nameObj.name) {
            localizedNames[nameObj.language.name] = nameObj.name;
          }
        });
        
        // 使用语言代码映射
        if (localizedNames[currentLanguage]) {
          return localizedNames[currentLanguage];
        }
        const fallbacks = LANGUAGE_CODE_MAPPING[currentLanguage] || ['en'];
        for (const lang of fallbacks) {
          if (localizedNames[lang]) {
            return localizedNames[lang];
          }
        }
        return localizedNames['en'] || abilityName;
      }
    }
  }

  try {
    const response = await fetch(`https://pokeapi.co/api/v2/ability/${abilityName}`);
    if (!response.ok) {
      return abilityName;
    }
    const abilityData = await response.json();
    
    // 缓存技能数据
    moveCache.set(cacheKey, { data: abilityData, timestamp: now });
    
    if (abilityData?.names) {
      const localizedNames: Record<string, string> = {};
      abilityData.names.forEach((nameObj: any) => {
        if (nameObj?.language?.name && nameObj.name) {
          localizedNames[nameObj.language.name] = nameObj.name;
        }
      });
      
      // 使用语言代码映射
      if (localizedNames[currentLanguage]) {
        return localizedNames[currentLanguage];
      }
      const fallbacks = LANGUAGE_CODE_MAPPING[currentLanguage] || ['en'];
      for (const lang of fallbacks) {
        if (localizedNames[lang]) {
          return localizedNames[lang];
        }
      }
      return localizedNames['en'] || abilityName;
    }
    
    return abilityName;
  } catch (error) {
    console.error(`Error fetching ability ${abilityName}:`, error);
    return abilityName;
  }
};

/**
 * 批量获取宝可梦技能的本地化名称
 */
export const fetchBatchAbilityLocalizations = async (pokemonList: Pokemon[], currentLanguage: string): Promise<void> => {
  // 收集所有唯一的技能名称
  const abilityNamesSet = new Set<string>();
  pokemonList.forEach(pokemon => {
    if (pokemon.abilities) {
      pokemon.abilities.forEach(ability => {
        abilityNamesSet.add(ability.ability.name);
      });
    }
  });

  if (abilityNamesSet.size === 0) return;

  console.debug(`Fetching localized names for ${abilityNamesSet.size} abilities...`);
  
  // 为每个宝可梦的每个技能获取本地化名称
  const fetchPromises = pokemonList.map(async (pokemon) => {
    if (!pokemon.abilities) return;
    
    for (const ability of pokemon.abilities) {
      if (!ability.ability.localizedNames) {
        ability.ability.localizedNames = {};
      }
      // 如果当前语言的名称已经存在，跳过
      if (ability.ability.localizedNames[currentLanguage]) {
        continue;
      }
      // 获取本地化名称
      const localizedName = await fetchAbilityLocalization(ability.ability.name, currentLanguage);
      ability.ability.localizedNames[currentLanguage] = localizedName;
    }
  });

  await Promise.all(fetchPromises);
  console.debug(`Ability localization complete.`);
};

/**
 * fetchBatchSpeciesData 函数用于最后获取多语言名称
 */
export const fetchBatchSpeciesData = async (pokemonList: Pokemon[]): Promise<void> => {
  const pokemonToFetch = pokemonList.filter(p => !p.localizedNames);

  if (pokemonToFetch.length === 0) return;

  console.debug(`Number of Pokémon needing localized names: ${pokemonToFetch.length}`);
  pokemonToFetch.forEach(p => { p.isLoadingNames = true; });

  const speciesPromises = pokemonToFetch.map(async (pokemon) => {
    try {
      const speciesId = pokemon.id;
      const cacheKey = `species_${speciesId}`; // Using speciesId for cache key consistency
      const now = Date.now();

      if (speciesCache.has(speciesId) && now - (speciesCache.get(speciesId)!.timestamp || 0) < CACHE_DURATION) {
        return { pokemon, species: speciesCache.get(speciesId)!.data as PokemonSpecies };
      }
      // Also check the more specific minimal cache key if used elsewhere, though this function fetches full species
      if (speciesCache.has(cacheKey) && now - (speciesCache.get(cacheKey)!.timestamp || 0) < CACHE_DURATION) {
         return { pokemon, species: speciesCache.get(cacheKey)!.data as PokemonSpecies };
      }


      const speciesUrl = `https://pokeapi.co/api/v2/pokemon-species/${speciesId}/`;
      const response = await fetch(speciesUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch species for ${pokemon.name} (ID ${speciesId}): ${response.status}`);
      }
      const speciesData = await response.json() as PokemonSpecies;

      speciesCache.set(speciesId, { data: speciesData, timestamp: now }); // Cache by ID for general use

      return { pokemon, species: speciesData };
    } catch (error) {
      console.error(`Error fetching species for ${pokemon.name} (ID ${pokemon.id}):`, error);
      return { pokemon, species: null }; // Ensure pokemon object is still returned for mapping later
    }
  });

  const results = await Promise.allSettled(speciesPromises);

  results.forEach((result) => {
    if (result.status === 'rejected') {
      // Error already logged in the map, result.reason might be redundant or too verbose here.
      // Consider if a summary error is needed or if individual logs are sufficient.
      console.error("A species data fetch failed:", result.reason);
      return;
    }

    const { pokemon, species } = result.value; // result.value should always exist for 'fulfilled'
    if (species && species.names) {
      const localizedNames: Record<string, string> = {};
      species.names.forEach(nameObj => {
        if (nameObj?.language?.name && nameObj.name) {
          localizedNames[nameObj.language.name.toLowerCase()] = nameObj.name;
        }
      });

      Object.entries(LANGUAGE_CODE_MAPPING).forEach(([appLangCode, apiLangCodes]) => {
        if (!localizedNames[appLangCode]) {
          for (const apiLangCode of apiLangCodes) {
            const normalizedApiLangCode = apiLangCode.toLowerCase();
            if (localizedNames[normalizedApiLangCode]) {
              localizedNames[appLangCode] = localizedNames[normalizedApiLangCode];
              break;
            }
          }
        }
      });

      if (Object.keys(localizedNames).length > 0) {
        pokemon.localizedNames = localizedNames;
      } else {
        console.warn(`No valid localized names found for Pokémon ID: ${pokemon.id}`);
        pokemon.localizedNames = {}; // Ensure property exists
      }
    } else {
      console.warn(`No valid species or names data fetched for Pokémon ID: ${pokemon.id}`);
      pokemon.localizedNames = {}; // Ensure property exists
    }
    pokemon.isLoadingNames = false;
  });

  console.debug(`Batch localized name processing complete.`);
};

/**
 * Fetch localized species names for specific ids (e.g. Pokédex list when base info cache is empty).
 * Mirrors the name mapping used in fetchBatchSpeciesData / getPokemonBasicInfoMap.
 */
export const fetchSpeciesLocalizedNamesByIds = async (
  ids: number[]
): Promise<Record<number, Record<string, string>>> => {
  const unique = Array.from(new Set(ids.filter((id) => id > 0)));
  const result: Record<number, Record<string, string>> = {};
  const concurrency = 5;

  const processOne = async (id: number) => {
    try {
      const response = await fetch(`https://pokeapi.co/api/v2/pokemon-species/${id}/`);
      if (!response.ok) return;
      const speciesData = await response.json();
      if (!speciesData?.names) return;

      const localizedNames: Record<string, string> = {};
      speciesData.names.forEach((nameObj: { language?: { name: string }; name?: string }) => {
        if (nameObj?.language?.name && nameObj.name) {
          localizedNames[nameObj.language.name.toLowerCase()] = nameObj.name;
        }
      });

      Object.entries(LANGUAGE_CODE_MAPPING).forEach(([appLangCode, apiLangCodes]) => {
        if (!localizedNames[appLangCode]) {
          for (const apiLangCode of apiLangCodes) {
            const normalizedApiLangCode = apiLangCode.toLowerCase();
            if (localizedNames[normalizedApiLangCode]) {
              localizedNames[appLangCode] = localizedNames[normalizedApiLangCode];
              break;
            }
          }
        }
      });

      if (Object.keys(localizedNames).length > 0) {
        result[id] = localizedNames;
      }
    } catch (error) {
      console.error(`fetchSpeciesLocalizedNamesByIds failed for ${id}:`, error);
    }
  };

  for (let i = 0; i < unique.length; i += concurrency) {
    const batch = unique.slice(i, i + concurrency);
    await Promise.all(batch.map(processOne));
  }

  return result;
};

/**
 * fetchPokemonTypes 函数逻辑不变
 */
export const fetchPokemonTypes = async (): Promise<PokemonType[]> => {
  const t = getTranslation();
  try {
    const standardTypes = [
      { name: 'normal', url: 'https://pokeapi.co/api/v2/type/1' },
      { name: 'fire', url: 'https://pokeapi.co/api/v2/type/10' },
      { name: 'water', url: 'https://pokeapi.co/api/v2/type/11' },
      { name: 'electric', url: 'https://pokeapi.co/api/v2/type/13' },
      { name: 'grass', url: 'https://pokeapi.co/api/v2/type/12' },
      { name: 'ice', url: 'https://pokeapi.co/api/v2/type/15' },
      { name: 'fighting', url: 'https://pokeapi.co/api/v2/type/2' },
      { name: 'poison', url: 'https://pokeapi.co/api/v2/type/4' },
      { name: 'ground', url: 'https://pokeapi.co/api/v2/type/5' },
      { name: 'flying', url: 'https://pokeapi.co/api/v2/type/3' },
      { name: 'psychic', url: 'https://pokeapi.co/api/v2/type/14' },
      { name: 'bug', url: 'https://pokeapi.co/api/v2/type/7' },
      { name: 'rock', url: 'https://pokeapi.co/api/v2/type/6' },
      { name: 'ghost', url: 'https://pokeapi.co/api/v2/type/8' },
      { name: 'dragon', url: 'https://pokeapi.co/api/v2/type/16' },
      { name: 'dark', url: 'https://pokeapi.co/api/v2/type/17' },
      { name: 'steel', url: 'https://pokeapi.co/api/v2/type/9' },
      { name: 'fairy', url: 'https://pokeapi.co/api/v2/type/18' }
    ];
    return standardTypes;
  } catch (error) {
    console.error("Error fetching Pokémon types:", error);
    toast.error(t(ERROR_MESSAGES.fetchTypesError));
    return [];
  }
};

/**
 * @description Fetches and caches a raw list of all Pokémon with their basic URLs.
 * Contains ID, name, pokemonUrl, and speciesUrl.
 * @returns {Promise<Array<{ id: number; name: string; pokemonUrl: string; speciesUrl: string; }>>}
 */
const getRawPokemonListCached = async (): Promise<Array<{ id: number; name: string; pokemonUrl: string; speciesUrl: string; }>> => {
  const now = Date.now();
  if (rawPokemonListCache.data.length > 0 && now - rawPokemonListCache.timestamp < RAW_LIST_CACHE_DURATION) {
    console.debug("🚀 Using cached raw Pokémon list.");
    return rawPokemonListCache.data;
  }

  // Attempt to load from localStorage first
  try {
    const cachedStr = localStorage.getItem('rawPokemonListCache_');
    if (cachedStr) {
      const cached = JSON.parse(cachedStr);
      if (cached.data && cached.timestamp && now - cached.timestamp < RAW_LIST_CACHE_DURATION) {
        console.debug("💾 Using raw Pokémon list from LocalStorage cache.");
        rawPokemonListCache.data = cached.data;
        rawPokemonListCache.timestamp = cached.timestamp;
        return rawPokemonListCache.data;
      }
    }
  } catch (e) {
    console.error("Failed to read raw Pokémon list from LocalStorage:", e);
    localStorage.removeItem('rawPokemonListCache_'); // Clear potentially corrupted data
  }

  console.debug("🛠️ Fetching raw Pokémon list from API...");
  try {
    const response = await fetch(`https://pokeapi.co/api/v2/pokemon?limit=1025`); // Max 1025 official Pokémon
    if (!response.ok) {
      throw new Error(`Failed to fetch raw pokemon list: ${response.status}`);
    }
    const listData = await response.json();
    const results: { name: string, url: string }[] = listData.results;

    const transformedList = results.map(basic => {
      const idMatch = basic.url.match(/\/(\d+)\/?$/);
      const id = idMatch ? parseInt(idMatch[1]) : 0; 
      return {
        id,
        name: basic.name,
        pokemonUrl: basic.url,
        speciesUrl: `https://pokeapi.co/api/v2/pokemon-species/${id}/`
      };
    }).filter(p => p.id > 0 && p.id <= 1025); 

    rawPokemonListCache.data = transformedList;
    rawPokemonListCache.timestamp = now;
    
    try {
        localStorage.setItem('rawPokemonListCache_', JSON.stringify(rawPokemonListCache));
        console.debug("💾 Raw Pokémon list saved to LocalStorage.");
    } catch (e) {
        console.error("Failed to save raw Pokémon list to LocalStorage:", e);
    }

    console.debug(`✅ Successfully fetched and cached ${transformedList.length} raw Pokémon entries.`);
    return transformedList;
  } catch (error) {
    console.error("❌ Failed to fetch or process raw Pokémon list:", error);
    toast.error("Failed to get initial Pokémon list. Please try again."); // User-facing
    return []; 
  }
};

export interface LocalPokemonEntry { // 与生成脚本中的定义一致
  id: number;
  name: string;
  spriteUrl: string;
  types: string[];
  generation: string;
  rarityScore: number;
  evolutionStage: 'fully-evolved' | 'not-fully-evolved' | 'single-stage' | null;
  height: number; // 添加身高 (单位：dm)
  weight: number; // 添加体重 (单位：hg)
}

let localPokemonDatabase: LocalPokemonEntry[] = [];
let isLocalDbLoading = false;
let localDbLoadPromise: Promise<boolean> | null = null;
let localDbIndex: null | {
  version: number;
  updatedAt?: string;
  total: number;
  partSize: number;
  partCount: number;
  parts: string[];
} = null;
let loadedPartCount = 0;

const ensureLocalDbIndex = async (): Promise<typeof localDbIndex> => {
  if (localDbIndex) return localDbIndex;
  try {
    const res = await fetch("/data/pokemon-db.index.json", { cache: "force-cache" });
    if (!res.ok) return null;
    const idx = await res.json();
    if (
      !idx ||
      typeof idx !== "object" ||
      typeof idx.total !== "number" ||
      typeof idx.partSize !== "number" ||
      !Array.isArray(idx.parts)
    ) {
      return null;
    }
    localDbIndex = idx;
    return localDbIndex;
  } catch {
    return null;
  }
};

export const getLocalPokemonDbMeta = () => ({
  total: localDbIndex?.total ?? localPokemonDatabase.length,
  loaded: localPokemonDatabase.length,
  loadedParts: loadedPartCount,
  partCount: localDbIndex?.partCount ?? loadedPartCount,
});

/** Serializes chunk loads so concurrent callers never fetch/append the same part twice. */
let loadMoreLocalDbChain: Promise<boolean> = Promise.resolve(true);

const loadOneMoreLocalPokemonDbPart = async (): Promise<boolean> => {
  const idx = await ensureLocalDbIndex();
  if (!idx) return false;
  if (loadedPartCount >= idx.partCount) return true;

  const nextPart = idx.parts[loadedPartCount];
  try {
    const response = await fetch(`/data/${nextPart}`, { cache: "force-cache" });
    if (!response.ok) return false;
    const data = await response.json();
    if (!Array.isArray(data) || data.length === 0) {
      loadedPartCount += 1;
      return true;
    }
    localPokemonDatabase = localPokemonDatabase.concat(data as LocalPokemonEntry[]);
    loadedPartCount += 1;
    return true;
  } catch {
    return false;
  }
};

export const loadMoreLocalPokemonDb = (): Promise<boolean> => {
  const pending = loadMoreLocalDbChain.then(() => loadOneMoreLocalPokemonDbPart());
  loadMoreLocalDbChain = pending.catch(() => false);
  return pending;
};

/**
 * Loads every remaining DB chunk in order (non-blocking for callers when not awaited on the UI path).
 * Use after search/filter so results can cover the full catalog without cold-starting all parts.
 */
export const prefetchRemainingLocalPokemonDb = async (onProgress?: () => void): Promise<void> => {
  const idx = await ensureLocalDbIndex();
  if (!idx) return;
  while (loadedPartCount < idx.partCount) {
    const ok = await loadMoreLocalPokemonDb();
    if (!ok) break;
    onProgress?.();
  }
};

/**
 * @description Loads the local Pokémon database from the JSON file.
 * Ensures it's only loaded once.
 * @returns {Promise<boolean>} True if loaded successfully or already loaded, false otherwise.
 */
export const loadLocalPokemonDb = async (): Promise<boolean> => {
  if (localPokemonDatabase.length > 0) {
    console.debug("📚 Local Pokémon DB already loaded.");
    return true;
  }
  if (isLocalDbLoading && localDbLoadPromise) {
    console.debug("📚 Local Pokémon DB is currently loading, awaiting existing promise.");
    return localDbLoadPromise;
  }

  isLocalDbLoading = true;
  localDbLoadPromise = (async () => {
    try {
      console.log("📚 Attempting to load local Pokémon DB (chunked)...");
      const idx = await ensureLocalDbIndex();
      if (idx) {
        // Load only the first chunk for fast cold start.
        loadedPartCount = 0;
        localPokemonDatabase = [];
        const ok = await loadMoreLocalPokemonDb();
        if (!ok || localPokemonDatabase.length === 0) {
          throw new Error("Failed to load first DB chunk.");
        }
        console.log(
          `✅ Local Pokémon DB loaded first chunk: ${localPokemonDatabase.length}/${idx.total} entries.`
        );
      } else {
        // Fallback to legacy single file if index is missing.
        console.log("📚 Falling back to legacy pokemon-db.json...");
        const response = await fetch("/data/pokemon-db.json", { cache: "force-cache" });
        if (!response.ok) {
          throw new Error(`Failed to fetch local DB: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        if (!Array.isArray(data) || data.length === 0) {
          throw new Error("Local DB is empty or not an array.");
        }
        localPokemonDatabase = data as LocalPokemonEntry[];
        loadedPartCount = 1;
        console.log(`✅ Local Pokémon DB loaded successfully with ${localPokemonDatabase.length} entries.`);
      }
      isLocalDbLoading = false;
      return true;
    } catch (error) {
      console.error("❌ Failed to load local Pokémon DB:", error);
      toast.error("Critical error: Could not load Pokémon data. Please try refreshing the page.");
      localPokemonDatabase = []; // Ensure it's empty on failure
      loadedPartCount = 0;
      isLocalDbLoading = false;
      return false;
    }
  })();
  return localDbLoadPromise;
};

/**
 * @description Gets the loaded local Pokémon database.
 * @returns {LocalPokemonEntry[]} The local database array, or an empty array if not loaded.
 */
export const getLocalPokemonDb = (): LocalPokemonEntry[] => {
  return localPokemonDatabase;
};

export const countMatchingPokemon = async (filters: PokemonFilters): Promise<number> => {
  console.debug("Counting matching Pokémon using local DB, filters:", filters);
  // const t = getTranslation(); // t() is available via getTranslation if needed for new messages

  const dbLoaded = await loadLocalPokemonDb();
  if (!dbLoaded || localPokemonDatabase.length === 0) {
    console.error("Cannot count Pokémon: Local DB not loaded.");
    return 0;
  }

  let candidateEntries: LocalPokemonEntry[] = localPokemonDatabase;

  if (filters.generation && filters.generation !== "any") {
    candidateEntries = candidateEntries.filter(p => p.generation === filters.generation);
  }
  if (filters.type && filters.type !== "any") {
    candidateEntries = candidateEntries.filter(p => p.types.includes(filters.type!));
  }
  if (filters.rarity && filters.rarity !== "any") {
    const targetRarity = parseInt(filters.rarity);
    candidateEntries = candidateEntries.filter(p => p.rarityScore >= targetRarity);
  }
  if (filters.evolutionStage && filters.evolutionStage !== "all") {
    candidateEntries = candidateEntries.filter(p => p.evolutionStage === filters.evolutionStage);
  }
  
  console.log(`✅ Count complete using local DB. Found ${candidateEntries.length} matching Pokémon.`);
  return candidateEntries.length;
};

const getRandomPokemonIds = (
  maxId: number,
  count: number,
  excludedIds: Set<number> = new Set()
): number[] => {
    const randomIds = new Set<number>();
    const availableCount = maxId - excludedIds.size;

    if (count >= availableCount && maxId > 0) {
      for (let i = 1; i <= maxId; i++) {
        if (!excludedIds.has(i)) {
          randomIds.add(i);
        }
      }
      return Array.from(randomIds).sort(() => 0.5 - Math.random()).slice(0, count);
    }
    if ((maxId === 0 || availableCount <= 0) && count > 0) return []; // No IDs to choose from
    if (count === 0) return [];

    while (randomIds.size < count) {
      const randomId = Math.floor(Math.random() * maxId) + 1;
      if (!excludedIds.has(randomId)) {
        randomIds.add(randomId);
      }
      if (randomIds.size >= availableCount) break; // Optimization: if all available IDs are picked
    }
    return Array.from(randomIds);
  };

export const fetchRandomPokemon = async (
  count: number,
  filters: PokemonFilters = {},
  options: FetchRandomPokemonOptions = {}
): Promise<Pokemon[]> => {
  console.debug("Generating random Pokémon, count:", count, "filters:", filters);
  const t = getTranslation();
  checkLanguageChange(); 
  const excludedIds = new Set(options.excludeIds ?? []);
  // cleanupExpiredCache(); // Review if still needed in the same way

  const PRELOAD_COUNT = 5; // For fetching a bit more than requested, if needed later for full data.
  const MAX_POKEMON_ID_FOR_NO_FILTER_RANDOM = 1025; // Used only if local DB fails and we fall back for no-filter

  const activeFilterKeys = Object.keys(filters).filter(key => {
    const filterKey = key as keyof PokemonFilters;
    return !(filters[filterKey] === undefined || filters[filterKey] === "" || filters[filterKey] === "any" || (filterKey === 'evolutionStage' && filters[filterKey] === 'all'));
  });
  const hasActiveFilters = activeFilterKeys.length > 0;

  if (!hasActiveFilters) {
    console.log(`🚀 No active filters, attempting to use local DB for random selection first...`);
    const dbLoaded = await loadLocalPokemonDb();
    if (dbLoaded && localPokemonDatabase.length > 0) {
        const availableEntries = localPokemonDatabase.filter(entry => !excludedIds.has(entry.id));
        let randomEntries: LocalPokemonEntry[];
        if (count >= availableEntries.length) {
            randomEntries = availableEntries.slice().sort(() => 0.5 - Math.random()).slice(0, count);
        } else {
            const tempDb = availableEntries.slice();
            randomEntries = [];
            for(let i = 0; i < count && tempDb.length > 0; i++) {
                const randomIndex = Math.floor(Math.random() * tempDb.length);
                randomEntries.push(tempDb.splice(randomIndex, 1)[0]);
            }
        }
        
        console.debug(`Selected ${randomEntries.length} random entries from local DB for no-filter case.`);
        if (randomEntries.length === 0 && count > 0) {
             toast.info("No Pokémon available for selection from local data.");
             return [];
        }
        if (randomEntries.length === 0 && count === 0) return [];

        const fetchFullPromises = randomEntries.map(entry =>
            fetchPokemon(entry.id).catch(error => {
              console.error(`Error fetching full data for Pokémon ${entry.id} (no-filter, local DB path):`, error);
              return null;
            })
        );
        const fullPokemonData = (await Promise.all(fetchFullPromises)).filter(p => p !== null) as Pokemon[];

        if (fullPokemonData.length > 0) {
            console.debug(`Fetching localized names for ${fullPokemonData.length} Pokémon (no-filter, local DB path)...`);
            await fetchBatchSpeciesData(fullPokemonData);
            console.debug("Localized names fetching complete (no-filter, local DB path).");
        }
        console.log(`✅ Successfully generated ${fullPokemonData.length} Pokémon (no-filter, using local DB).`);
        return fullPokemonData;
    } else {
        // Fallback to old direct ID generation if local DB failed to load
        console.warn("Local DB not available for no-filter case, falling back to random ID generation.");
        try {
            const randomIds = getRandomPokemonIds(MAX_POKEMON_ID_FOR_NO_FILTER_RANDOM, count, excludedIds);
            // ... (rest of the no-filter direct ID fetching logic - copied & adapted from previous version) ...
            if (randomIds.length === 0 && count > 0) {
              console.warn("Failed to generate any random Pokémon IDs (fallback).");
              toast.info("No Pokémon available for selection (fallback)."); 
              return [];
            }
            if (randomIds.length === 0 && count === 0) return [];
      
            const fetchPromises = randomIds.map(id =>
              fetchPokemon(id).catch(error => {
                console.error(`Error fetching Pokémon ${id} full data (fallback):`, error);
                return null; 
              })
            );
            let fetchedPokemons = (await Promise.all(fetchPromises)).filter(p => p !== null) as Pokemon[];
      
            if (fetchedPokemons.length === 0 && count > 0) {
              console.warn("Failed to fetch data for any random Pokémon (fallback).");
              toast.error("Failed to fetch random Pokémon, please try again later (fallback)."); 
              return [];
            }
            if (fetchedPokemons.length < count && fetchedPokemons.length > 0) {
              toast.info(`Successfully fetched ${fetchedPokemons.length} random Pokémon (requested ${count}) (fallback).`); 
            }
            console.debug(`Successfully fetched full data for ${fetchedPokemons.length} random Pokémon (fallback).`);
            if (fetchedPokemons.length > 0) {
              console.debug(`Fetching localized names for ${fetchedPokemons.length} random Pokémon (fallback)...`);
              await fetchBatchSpeciesData(fetchedPokemons);
              console.debug("Localized names fetching complete for random Pokémon (fallback).");
            }
            console.log(`✅ Direct fetch for no-filter scenario complete (fallback), returning ${fetchedPokemons.length} Pokémon.`);
            return fetchedPokemons;
        } catch (error) {
            console.error("❌ Error during direct fetch of random Pokémon (fallback):", error);
            toast.error("Error fetching random Pokémon (fallback)."); 
            return [];
        }
    }
  }

  // --- Active filters: Use local DB --- 
  console.log("🔍 Active filters present, using local DB for filtering...");
  const dbLoaded = await loadLocalPokemonDb(); // Ensure it's loaded
  if (!dbLoaded || localPokemonDatabase.length === 0) {
    console.error("Cannot generate Pokémon with filters: Local DB not loaded.");
    toast.error("Pokémon data is not available for filtering. Please try refreshing.");
    return [];
  }

  let candidateEntries: LocalPokemonEntry[] = localPokemonDatabase;

  if (filters.generation && filters.generation !== "any") {
    candidateEntries = candidateEntries.filter(p => p.generation === filters.generation);
  }
  if (filters.type && filters.type !== "any") {
    candidateEntries = candidateEntries.filter(p => p.types.includes(filters.type!));
  }
  if (filters.rarity && filters.rarity !== "any") {
    const targetRarity = parseInt(filters.rarity);
    candidateEntries = candidateEntries.filter(p => p.rarityScore >= targetRarity);
  }
  if (filters.evolutionStage && filters.evolutionStage !== "all") {
    candidateEntries = candidateEntries.filter(p => p.evolutionStage === filters.evolutionStage);
  }
  if (excludedIds.size > 0) {
    candidateEntries = candidateEntries.filter(p => !excludedIds.has(p.id));
  }
  console.debug(`After filtering local DB: ${candidateEntries.length} candidates.`);

  if (candidateEntries.length === 0) {
    toast.warning(t(ERROR_MESSAGES.noMatchingPokemon));
    return [];
  }

  const shuffledEntries = candidateEntries.slice().sort(() => 0.5 - Math.random());
  const actualDisplayCount = Math.min(count, shuffledEntries.length);
  const selectedEntries = shuffledEntries.slice(0, actualDisplayCount);

  if (candidateEntries.length < count && count > 0 && candidateEntries.length > 0) {
     toast.info(t(ERROR_MESSAGES.partialMatchingPokemon, { requested: count.toString(), total: candidateEntries.length.toString() }));
  }

  if (selectedEntries.length === 0) {
    // This case should be caught by the earlier check, but as a safeguard:
    // No specific toast here as the previous one would have covered it.
    console.warn("No entries selected for display, though candidates existed.");
    return [];
  }
  
  console.debug(`Selected ${selectedEntries.length} entries from local DB. Fetching full data for them...`);

  const fetchFullPromises = selectedEntries.map(entry =>
    fetchPokemon(entry.id).catch(error => {
      console.error(`Error fetching full data for Pokémon ${entry.id} (filtered path):`, error);
      return null;
    })
  );

  const fullPokemonData = (await Promise.all(fetchFullPromises)).filter(p => p !== null) as Pokemon[];

  if (fullPokemonData.length === 0 && selectedEntries.length > 0) {
      console.warn("Failed to fetch full data for any of the selected entries (filtered path).");
      toast.error("Error retrieving details for selected Pokémon.");
      return [];
  }

  if (fullPokemonData.length > 0) {
    console.debug(`Fetching localized names for ${fullPokemonData.length} Pokémon (filtered path)...`);
    await fetchBatchSpeciesData(fullPokemonData);
    console.debug("Localized names fetching complete (filtered path).");
  }

  console.log(`✅ Successfully generated ${fullPokemonData.length} Pokémon using local DB and API for details.`);
  return fullPokemonData;
};